import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import CollapseArrowIcon from "../../../../../../../assets/svg/collapse-arrow-icon";
import FlaggedIcon from "../../../../../../../assets/svg/flagged-icon";
import SortByIcon from "../../../../../../../assets/svg/sort-by-icon";
import Table from "../../../../../../../components/Table/Table";
import Pagination from "../../../../../../../components/Table/Pagination";
import { TicketStageSortingPopper } from "./TicketStageSortingPopper";
import { SORT_OPTION_MAP, PRIORITY_FILTER_OPTIONS } from "../utils/constant";
import { HelpDeskStage, HelpDeskTicket, StageSortOption } from "../utils/types";
import { getTicketTableColumns, getPriorityColor } from "../utils/functions";
import {
  useGetStageTicketsQuery,
  useUpdateTicketMutation,
  useGetTicketCategoriesQuery,
} from "../../../../../../../redux/services/helpdeskApi";
import { useOrgUsersList } from "../../../../../../../hooks/useOrgUsersList";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { TicketTableRowActions } from "./TicketTableRowActions";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../../../../../../redux/store";
import {
  DEFAULT_TABLE_FILTERS,
  setHelpdeskAllStagesPriorityFilter,
  setHelpdeskAllStagesAssigneesFilter,
  setHelpdeskTableColumnSort,
  setHelpdeskTableCurrentPage,
  setHelpdeskTableRowsPerPage,
  resetHelpdeskTableColumnSort,
} from "../../../../../../../redux/slices/helpdeskSlice";
import { type TicketAgent } from "../../TicketDetails/components/TicketOverview/utils/constants";
import {
  isHelpdeskFeatureRequestsClient,
  isHelpdeskResolutionClient,
} from "../../../utils/function";
import { getClientDomain } from "../../../../../../../utils/functions/client-code";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";
import {
  AgentAssignmentDropdown,
  type AgentUser,
} from "../../../../AgentAssignmentDropdown";
import { useHelpdeskUpdateAccessChecker } from "../../../utils/helpdeskPermissions";

interface TicketTableListingProps {
  stage: HelpDeskStage;
  selectedViewId?: string;
  systemNameFilter?: string;
}

export const TicketTableListing = ({
  stage,
  selectedViewId,
  systemNameFilter,
}: TicketTableListingProps) => {
  const dispatch = useDispatch();

  const stageFilters = useSelector(
    (state: RootState) =>
      state.helpdesk.tableFilters[stage?.key] ?? DEFAULT_TABLE_FILTERS
  );
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );

  const {
    priorityFilter: appliedPriority,
    assigneesFilter: appliedAssignees,
    columnSort,
    currentPage,
    rowsPerPage,
  } = stageFilters;

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [tempPriority, setTempPriority] = useState<string[]>(appliedPriority);
  const [tickets, setTickets] = useState<HelpDeskTicket[]>(
    stage?.tickets ?? []
  );
  const [totalTickets, setTotalTickets] = useState(stage?.total ?? 0);

  const tableRef = useRef<HTMLDivElement>(null);

  const scrollPopupIntoView = useCallback(() => {
    tableRef.current?.scrollIntoView({ block: "nearest", behavior: "instant" });

    const openDropdown = tableRef.current?.querySelector(
      "[data-table-dropdown], [data-sort-dropdown]"
    ) as HTMLElement | null;
    const tableColumnsContainer = openDropdown?.closest(
      "[data-table-columns]"
    ) as HTMLElement | null;
    if (openDropdown && tableColumnsContainer) {
      const dropdownRect = openDropdown.getBoundingClientRect();
      const containerRect = tableColumnsContainer.getBoundingClientRect();
      const overflowRight = dropdownRect.right - containerRect.right;
      const overflowLeft = containerRect.left - dropdownRect.left;
      if (overflowRight > 0) {
        tableColumnsContainer.scrollLeft += overflowRight + 16;
      } else if (overflowLeft > 0) {
        tableColumnsContainer.scrollLeft -= overflowLeft + 16;
      }
    }
  }, []);

  const navigate = useNavigate();

  const { activeUsers } = useOrgUsersList();
  const agents = useMemo<TicketAgent[]>(
    () =>
      activeUsers.map((user) => ({
        id: user._id,
        name: user.name,
        role: user.role,
        status: user.status,
      })),
    [activeUsers]
  );

  const agentsList = useMemo<AgentUser[]>(
    () =>
      activeUsers.map((user) => ({
        id: user._id,
        name: user.name,
        email: user.email,
        role: user.role,
        status: user.status,
      })),
    [activeUsers]
  );

  const [updateTicket] = useUpdateTicketMutation();
  const { data: ticketCategoriesData } = useGetTicketCategoriesQuery();

  const handleAssigneesUpdate = useCallback(
    async (ticketId: number, assignees: string[]) => {
      try {
        await updateTicket({
          ticketId: String(ticketId),
          assignees,
        }).unwrap();
        setTickets((prev) =>
          prev.map((t) => (t.ticket_id === ticketId ? { ...t, assignees } : t))
        );
      } catch (error) {
        console.error("Failed to update assignees:", error);
      }
    },
    [updateTicket]
  );

  const activeSort = columnSort?.updated_at || columnSort?.created_at;
  const sortParams = activeSort
    ? SORT_OPTION_MAP[activeSort as StageSortOption]
    : null;

  const needsApi =
    currentPage > 1 ||
    !!activeSort ||
    appliedPriority?.length > 0 ||
    appliedAssignees?.length > 0 ||
    rowsPerPage !== 10;

  const viewIdParam =
    selectedViewId && selectedViewId !== "all_tickets"
      ? selectedViewId
      : undefined;

  const { data: paginatedData, isFetching } = useGetStageTicketsQuery(
    {
      stage_key: stage?.key,
      page: currentPage,
      limit: rowsPerPage,
      sort_key: sortParams?.sort_key,
      sort_order: sortParams?.sort_order,
      priority: appliedPriority?.length > 0 ? appliedPriority : [],
      assignees: appliedAssignees?.length > 0 ? appliedAssignees : [],
      ...(viewIdParam && { view_id: viewIdParam }),
      ...(systemNameFilter && { system_name: systemNameFilter }),
    },
    { skip: !needsApi }
  );

  const totalPages = useMemo(
    () =>
      needsApi ? (paginatedData?.total_pages ?? 1) : (stage?.total_pages ?? 1),
    [needsApi, paginatedData?.total_pages, stage?.total_pages]
  );

  useEffect(() => {
    if (needsApi && paginatedData) {
      setTickets(paginatedData?.tickets ?? []);
      setTotalTickets(paginatedData?.total ?? 0);
    } else if (!needsApi) {
      setTickets(stage?.tickets ?? []);
      setTotalTickets(stage?.total ?? 0);
    }
  }, [needsApi, paginatedData, stage?.tickets, stage?.total]);

  const onTicketDeleted = useCallback((ticketId: number) => {
    setTickets((prev) => prev?.filter((t) => t?.ticket_id !== ticketId));
    setTotalTickets((prev) => Math.max(0, prev - 1));
  }, []);

  const handleToggleDropdown = useCallback(
    (column: string) => {
      setActiveDropdown((prev) => (prev === column ? null : column));
      if (column === "priority") {
        setTempPriority(appliedPriority);
      }
    },
    [appliedPriority]
  );

  const handleSort = useCallback(
    (column: string, option: StageSortOption) => {
      const isToggleOff = columnSort?.[column] === option;
      dispatch(
        setHelpdeskTableColumnSort({
          stageKey: stage?.key,
          columnSort: {
            updated_at: null,
            created_at: null,
            [column]: isToggleOff ? null : option,
          },
        })
      );
      setActiveDropdown(null);
    },
    [columnSort, dispatch, stage?.key]
  );

  const handleResetSort = useCallback(
    (column: string) => {
      dispatch(resetHelpdeskTableColumnSort({ stageKey: stage?.key, column }));
      setActiveDropdown(null);
    },
    [dispatch, stage?.key]
  );

  const handleResetPriority = useCallback(() => {
    setTempPriority([]);
    dispatch(setHelpdeskAllStagesPriorityFilter([]));
    setActiveDropdown(null);
  }, [dispatch]);

  const handleRowClick = useCallback(
    (ticket: HelpDeskTicket) => {
      navigate(`/dashboard/crm/helpdesk/all-tickets/${ticket?.ticket_id}`);
    },
    [navigate]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      if (page < 1) return;
      dispatch(setHelpdeskTableCurrentPage({ stageKey: stage?.key, page }));
    },
    [dispatch, stage?.key]
  );

  const handleRowsPerPageChange = useCallback(
    (size: number) => {
      dispatch(
        setHelpdeskTableRowsPerPage({ stageKey: stage?.key, rowsPerPage: size })
      );
    },
    [dispatch, stage?.key]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event?.target as Element;
      if (
        !target?.closest?.("[data-table-dropdown]") &&
        !target?.closest?.("[data-table-dropdown-trigger]") &&
        !target?.closest?.("[data-sort-dropdown]")
      ) {
        setActiveDropdown(null);
      }
    };

    const scrollContainer = document.querySelector(
      "[data-helpdesk-scroll-container]"
    ) as HTMLElement | null;
    if (activeDropdown) {
      scrollPopupIntoView();
      if (scrollContainer) scrollContainer.style.overflow = "hidden";
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      if (scrollContainer) scrollContainer.style.overflow = "";
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (scrollContainer) scrollContainer.style.overflow = "";
    };
  }, [activeDropdown, scrollPopupIntoView]);

  const actionsColumn: ColumnDef<HelpDeskTicket> = useMemo(
    () => ({
      id: "actions",
      size: 80,
      header: () => <div className="px-3 py-2" />,
      cell: ({ row }) => (
        <TicketTableRowActions
          ticket={row?.original}
          onTicketDeleted={onTicketDeleted}
        />
      ),
    }),
    [onTicketDeleted]
  );

  const handleScrollToggle = useCallback((lock: boolean) => {
    const scrollContainer = document.querySelector(
      "[data-helpdesk-scroll-container]"
    ) as HTMLElement | null;
    if (lock) {
      tableRef.current?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
      if (scrollContainer) scrollContainer.style.overflow = "hidden";
    } else {
      if (scrollContainer) scrollContainer.style.overflow = "";
    }
  }, []);

  const helpdeskLabels = getHelpdeskLabels(region);
  const isResolutionEnabled = isHelpdeskResolutionClient(getClientDomain());
  const canUpdate = useHelpdeskUpdateAccessChecker();

  const { tabId } = useParams();
  const [searchParams] = useSearchParams();
  const isFeatureRequestScope =
    isHelpdeskFeatureRequestsClient(getClientDomain()) &&
    tabId === "all-tickets" &&
    searchParams.get("helpdesk_filter") === "feature-request";

  const isViewSelected = !!selectedViewId && selectedViewId !== "all_tickets";

  const columns = useMemo(() => {
    const hasData = tickets?.length > 0;
    const baseColumns = getTicketTableColumns(
      agents,
      agentsList,
      handleAssigneesUpdate,
      handleScrollToggle,
      {
        isResolutionEnabled,
        stageKey: stage?.key,
        canUpdate,
        ticketCategories: ticketCategoriesData,
        region,
      }
    );
    const visibleBaseColumns = isFeatureRequestScope
      ? baseColumns?.filter(
          (col) => (col as { accessorKey?: string })?.accessorKey !== "priority"
        )
      : baseColumns;
    const mapped = visibleBaseColumns?.map((col) => {
      const accessorKey = (col as { accessorKey?: string })?.accessorKey ?? "";

      if (accessorKey === "priority") {
        if (isViewSelected) {
          return {
            ...col,
            header: () => (
              <div className="text-left px-3 py-2">
                <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
                  Priority
                </span>
              </div>
            ),
          } as ColumnDef<HelpDeskTicket>;
        }
        const filterCount = appliedPriority?.length;
        const isInteractive = hasData || filterCount > 0;
        return {
          ...col,
          header: () => (
            <div className="relative">
              <div
                data-table-dropdown-trigger
                className={`flex items-center gap-1 px-3 py-2 ${isInteractive ? "cursor-pointer" : ""}`}
                onClick={() =>
                  isInteractive && handleToggleDropdown("priority")
                }
              >
                {filterCount > 0 && (
                  <div className="w-4 h-4 bg-verba-blue dark:bg-verba-blue-dk rounded-full flex items-center justify-center">
                    <span className="text-fixed-white dark:text-fixed-white-dk text-xxs font-semibold leading-none">
                      {filterCount}
                    </span>
                  </div>
                )}
                <span className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
                  Priority
                </span>
                {isInteractive && (
                  <SortByIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
                )}
              </div>
              {activeDropdown === "priority" && (
                <div
                  data-table-dropdown
                  className="absolute top-10 left-0 w-[200px] border border-border-gray dark:border-border-gray-dk bg-popper-bg-fixed-white dark:bg-popper-bg-fixed-white-dk rounded-lg shadow-popper-blur-effect backdrop-blur-[20px] overflow-hidden z-50"
                >
                  <div className="flex items-center justify-between p-3">
                    <span className="text-sm font-semibold text-data-table-text dark:text-data-table-text-dk">
                      PRIORITY
                    </span>
                    <button
                      className="text-sm font-semibold text-verba-blue dark:text-verba-blue-dk hover:opacity-70 transition-opacity cursor-pointer"
                      onClick={handleResetPriority}
                    >
                      Reset
                    </button>
                  </div>
                  {PRIORITY_FILTER_OPTIONS?.map((opt, idx) => {
                    const isSelected = tempPriority?.includes(opt?.key);
                    return (
                      <div
                        key={opt?.key}
                        className={`flex items-center justify-between p-3 h-[36px] cursor-pointer ${
                          idx !== PRIORITY_FILTER_OPTIONS?.length - 1 &&
                          "border-b border-border-gray dark:border-border-gray-dk"
                        } ${
                          isSelected
                            ? "bg-card-highlight dark:bg-card-highlight-dk"
                            : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                        }`}
                        onClick={() =>
                          setTempPriority((prev) =>
                            prev?.includes(opt?.key)
                              ? prev?.filter((k) => k !== opt?.key)
                              : [...prev, opt?.key]
                          )
                        }
                      >
                        <div className="flex items-center gap-[2px]">
                          <FlaggedIcon
                            className={`w-3 h-3 shrink-0 ${getPriorityColor(opt?.label)}`}
                          />
                          <span
                            className={`text-sm font-medium ${getPriorityColor(opt?.label)}`}
                          >
                            {opt?.label}
                          </span>
                        </div>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          className="w-3.5 h-3.5 shrink-0 accent-verba-blue cursor-pointer rounded-sm appearance-none border border-gray-placeholder dark:border-gray-placeholder-dk bg-transparent checked:appearance-auto"
                        />
                      </div>
                    );
                  })}
                  <div className="flex items-center gap-2 h-[40px] p-3 border-t border-border-gray dark:border-border-gray-dk">
                    <button
                      className="flex-1 h-[26px] flex items-center justify-center px-2 py-1.5 rounded-md text-md font-semibold text-heading-text dark:text-heading-text-dk cursor-pointer"
                      onClick={() => {
                        setTempPriority([]);
                        setActiveDropdown(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="flex-1 h-[26px] flex items-center justify-center px-2 py-1.5 rounded-md bg-verba-indigo dark:bg-verba-indigo-dk border border-verba-indigo dark:border-verba-indigo-dk text-md font-semibold text-pure-white dark:text-pure-white-dk cursor-pointer"
                      onClick={() => {
                        dispatch(
                          setHelpdeskAllStagesPriorityFilter(tempPriority)
                        );
                        setActiveDropdown(null);
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          ),
        } as ColumnDef<HelpDeskTicket>;
      }

      if (accessorKey === "assignees") {
        if (isViewSelected) {
          return {
            ...col,
            header: () => (
              <div className="text-left px-3 py-2">
                <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
                  Assignees
                </span>
              </div>
            ),
          } as ColumnDef<HelpDeskTicket>;
        }
        const filterCount = appliedAssignees?.length;
        return {
          ...col,
          header: () => (
            <AgentAssignmentDropdown
              selectedAgentIds={appliedAssignees}
              maxAgents={0}
              canSaveEmpty
              showReset
              onReset={() => {
                dispatch(setHelpdeskAllStagesAssigneesFilter([]));
              }}
              onApply={async (ids) => {
                dispatch(setHelpdeskAllStagesAssigneesFilter(ids));
              }}
              onToggleScroll={handleScrollToggle}
              agentsList={agentsList}
              title="SELECT ASSIGNEES"
              subHeading={helpdeskLabels.filterByAssignee}
              applyButtonText="Apply"
              hideRole
              customTrigger={
                <div className="flex items-center gap-1 px-3 py-2 cursor-pointer">
                  {filterCount > 0 && (
                    <div className="w-4 h-4 bg-verba-blue dark:bg-verba-blue-dk rounded-full flex items-center justify-center">
                      <span className="text-fixed-white dark:text-fixed-white-dk text-xxs font-semibold leading-none">
                        {filterCount}
                      </span>
                    </div>
                  )}
                  <span className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
                    Assignees
                  </span>
                  {(hasData || filterCount > 0) && (
                    <SortByIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
                  )}
                </div>
              }
            />
          ),
        } as ColumnDef<HelpDeskTicket>;
      }

      if (accessorKey === "updated_at" || accessorKey === "created_at") {
        const label =
          accessorKey === "updated_at" ? "Last Activity" : "Created";
        const hasSortActive = !!columnSort?.[accessorKey];
        const isSortInteractive = hasData || hasSortActive;
        return {
          ...col,
          header: () => (
            <div className="relative">
              <div
                data-table-dropdown-trigger
                className={`flex items-center gap-1 px-3 py-2 ${isSortInteractive ? "cursor-pointer" : ""}`}
                onClick={() =>
                  isSortInteractive && handleToggleDropdown(accessorKey)
                }
              >
                <span className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
                  {label}
                </span>
                {isSortInteractive && (
                  <SortByIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
                )}
              </div>
              {activeDropdown === accessorKey && (
                <TicketStageSortingPopper
                  activeSort={columnSort?.[accessorKey]}
                  onSort={(option) => handleSort(accessorKey, option)}
                  onReset={() => handleResetSort(accessorKey)}
                  positionClassName="top-10 left-0"
                  isAlphaSort={false}
                />
              )}
            </div>
          ),
        } as ColumnDef<HelpDeskTicket>;
      }

      return col as ColumnDef<HelpDeskTicket>;
    });
    return [...mapped, actionsColumn];
  }, [
    agents,
    agentsList,
    handleAssigneesUpdate,
    handleScrollToggle,
    actionsColumn,
    tickets,
    appliedPriority,
    appliedAssignees,
    tempPriority,
    activeDropdown,
    columnSort,
    handleToggleDropdown,
    handleSort,
    handleResetSort,
    handleResetPriority,
    dispatch,
    stage?.key,
    isResolutionEnabled,
    canUpdate,
    ticketCategoriesData,
    isFeatureRequestScope,
    isViewSelected,
    region,
  ]);

  return (
    <div ref={tableRef} className="mb-4">
      <div
        className="flex items-center gap-3 mb-2 cursor-pointer select-none w-max"
        onClick={() => setIsCollapsed((prev) => !prev)}
      >
        <CollapseArrowIcon
          className={`w-4 h-4 shrink-0 text-verba-indigo dark:text-verba-indigo-dk transition-transform duration-200 ${
            isCollapsed ? "-rotate-90" : ""
          }`}
        />
        <button
          className="flex items-center gap-2.5 px-3 py-1 rounded-lg"
          style={{ backgroundColor: stage?.secondary_hex_code }}
        >
          <span className="text-md font-semibold text-white tracking-wide">
            {stage?.title?.toUpperCase()}
          </span>
          <span className="text-sm font-medium text-white">{totalTickets}</span>
        </button>
      </div>

      {!isCollapsed && (
        <div className="min-h-0">
          <Table
            COLUMNS={columns}
            COLUMNS_DATA={tickets}
            ariaLabel={`${stage?.key?.toUpperCase()} ${helpdeskLabels.ticketPluralLower} table`}
            cellClassName="!p-0 !h-[44px] cursor-pointer"
            isLoading={isFetching}
            rowCount={7}
            onRowClick={handleRowClick}
          />
          {totalPages > 0 && tickets?.length > 0 && (
            <div className="mt-3">
              <Pagination
                rowsPerPage={rowsPerPage}
                handleRowsPerPageChange={handleRowsPerPageChange}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
                totalPages={totalPages}
                rowsPerPageText={getHelpdeskLabels(region).paginationUnit}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
